import { Alert, Stack } from "@mui/material";
import { z } from "zod";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  AutoRecipeDefaultsContext,
  SearchType,
  useRevertAutoRecipeDefaultsMutation,
} from "store/api/generatedApi";
import { GetAutoRecipeDefaultsResponse } from "store/api/generatedZodApi/auto-recipe-defaults";
import { RevertDefaultsButton } from "./RevertDefaultsButton";

const recoveryErrorSchema = z.object({
  status: z.literal(409),
  data: z.object({
    detail: z.object({
      message: z.string(),
      context: GetAutoRecipeDefaultsResponse.shape.context,
    }),
  }),
});

export const getAutoRecipeRecoveryContext = ({
  error,
  searchType,
}: {
  error: unknown;
  searchType: SearchType;
}): AutoRecipeDefaultsContext | null => {
  const parsed = recoveryErrorSchema.safeParse(error);
  if (!parsed.success) return null;
  const { context } = parsed.data.data.detail;
  return context.search_type === searchType ? context : null;
};

export const AutoRecipeDefaultsRecovery = ({
  context,
  refresh,
  isFetching,
}: {
  context: AutoRecipeDefaultsContext;
  refresh: () => Promise<unknown>;
  isFetching: boolean;
}) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notify = useNotifyStatus();
  const [revert, { isLoading }] = useRevertAutoRecipeDefaultsMutation();
  const onRevert = async () => {
    try {
      await revert({
        tenantName,
        autoRecipeDefaultsContext: context,
      }).unwrap();
    } catch {
      notify({ type: "error", text: t("autoSaveFailed") });
    } finally {
      await refresh();
    }
  };
  return (
    <Stack gap={2}>
      <Alert severity="warning">{t("autoDefaultsUnsupported")}</Alert>
      <RevertDefaultsButton
        key={JSON.stringify(context)}
        disabled={isLoading || isFetching}
        warning={t("autoRestoreDefaultsWarning")}
        onRevert={() => {
          void onRevert();
        }}
      />
    </Stack>
  );
};
